"use client";

import type { ButtonHTMLAttributes, CSSProperties, KeyboardEvent, ReactNode, Ref } from "react";
import { cn } from "@/lib/utils";
import { Glyph } from "./Glyph";
import { GlassPanel } from "./GlassPanel";
import { FOCUS_RING } from "./focusRing.constants";
const SHELF_ITEM_SELECTOR = "[data-shelf-item]";
export function IslandChip({
  icon,
  isOpen,
  label,
  className,
  children,
  ref,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  icon?: ReactNode;
  isOpen: boolean;
  label: string;
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}) {
  return <div className={cn("relative flex h-8 min-w-0 items-center gap-2 rounded-full pl-1 pr-2.5 text-ink", "transition-colors duration-fast hover:bg-ink/10 has-[:focus-visible]:shadow-focus", isOpen && "bg-ink/10", className)} data-sentry-component="IslandChip" data-sentry-source-file="AppShelf.tsx">
            {icon && <span className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center overflow-hidden rounded-small">
                    {icon}
                </span>}
            {children}
            <Glyph name="chevronDown" className={cn("h-3.5 w-3.5 flex-shrink-0 text-ink-2 transition-transform duration-base", isOpen && "rotate-180")} data-sentry-element="Glyph" data-sentry-source-file="AppShelf.tsx" />
            <button ref={ref} type="button" aria-label={label} aria-haspopup="dialog" aria-expanded={isOpen} className="absolute inset-0 rounded-full focus-visible:outline-none" {...props} />
        </div>;
}
export function ShelfTile({
  icon,
  label,
  isOpen = false,
  isCurrent = false,
  onSelect,
  onPointerEnter
}: {
  icon: ReactNode;
  label: string;
  isOpen?: boolean;
  isCurrent?: boolean;
  onSelect: () => void;
  onPointerEnter?: () => void;
}) {
  return <button type="button" data-shelf-item="" aria-current={isCurrent ? "page" : undefined} onClick={onSelect} onPointerEnter={onPointerEnter} className={cn("relative flex w-[72px] flex-col items-center gap-1 rounded-xl pb-2 pt-1.5", "transition-colors duration-fast hover:bg-ink/[0.05]", FOCUS_RING)} data-sentry-component="ShelfTile" data-sentry-source-file="AppShelf.tsx">
            <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl shadow-card">
                {icon}
            </span>
            <span className="max-w-[68px] truncate text-caption text-ink/80">
                {label}
            </span>
            {isOpen && <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-ink" aria-hidden />}
        </button>;
}
export function ShelfRecord({
  icon,
  title,
  kind,
  isCurrent = false,
  closeLabel,
  onSelect,
  onClose
}: {
  icon: ReactNode;
  title: string;
  kind: string;
  isCurrent?: boolean;
  closeLabel: string;
  onSelect: () => void;
  onClose: () => void;
}) {
  return <div className={cn("flex h-11 min-w-0 max-w-[240px] items-center rounded-xl pr-1", isCurrent ? "bg-ink/[0.06]" : "hover:bg-ink/[0.04]")} data-sentry-component="ShelfRecord" data-sentry-source-file="AppShelf.tsx">
            <button type="button" data-shelf-item="" aria-current={isCurrent ? "page" : undefined} onClick={onSelect} className={cn("flex h-full min-w-0 flex-1 items-center gap-2 rounded-xl pl-1.5 pr-1 text-left", FOCUS_RING)}>
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-small">
                    {icon}
                </span>
                <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate text-callout font-medium text-ink">
                        {title}
                    </span>
                    <span className="truncate text-caption text-ink-3">
                        {kind}
                    </span>
                </span>
            </button>
            <button type="button" onClick={event => {
      focusNeighbourShelfItem(event.currentTarget);
      onClose();
    }} aria-label={closeLabel} title={closeLabel} className={cn("flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-ink-3 hover:bg-ink/[0.06] hover:text-ink", FOCUS_RING)}>
                <Glyph name="close" className="h-3 w-3" data-sentry-element="Glyph" data-sentry-source-file="AppShelf.tsx" />
            </button>
        </div>;
}
function moveShelfFocus(event: KeyboardEvent<HTMLDivElement>) {
  const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
  if (!keys.includes(event.key)) return;
  const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(SHELF_ITEM_SELECTOR));
  if (items.length === 0) return;
  event.preventDefault();
  const current = items.findIndex(item => item === document.activeElement);
  const last = items.length - 1;
  const next = event.key === "Home" ? 0 : event.key === "End" ? last : event.key === "ArrowRight" || event.key === "ArrowDown" ? current >= last ? 0 : current + 1 : current <= 0 ? last : current - 1;
  items[next]?.focus();
}
function focusNeighbourShelfItem(closeButton: HTMLElement) {
  const toolbar = closeButton.closest('[role="toolbar"]');
  const ownItem = closeButton.parentElement?.querySelector<HTMLElement>(SHELF_ITEM_SELECTOR);
  if (!toolbar || !ownItem) return;
  const items = Array.from(toolbar.querySelectorAll<HTMLElement>(SHELF_ITEM_SELECTOR));
  const index = items.indexOf(ownItem);
  (items[index + 1] ?? items[index - 1])?.focus();
}
export function focusShelfItem(panel: HTMLElement | null) {
  if (!panel) return;
  const current = panel.querySelector<HTMLElement>(`${SHELF_ITEM_SELECTOR}[aria-current="page"]`);
  const first = panel.querySelector<HTMLElement>(SHELF_ITEM_SELECTOR);
  (current ?? first)?.focus();
}
export function ShelfPanel({
  openLabel,
  records,
  apps,
  className,
  ref,
  ...props
}: {
  openLabel: string;
  records?: ReactNode;
  apps: ReactNode;
  className?: string;
  id?: string;
  "aria-label"?: string;
  style?: CSSProperties;
  ref?: Ref<HTMLDivElement>;
}) {
  return <GlassPanel ref={ref} role="dialog" className={cn("max-w-[calc(100vw-16px)] p-2.5", className)} {...props} data-sentry-element="GlassPanel" data-sentry-component="ShelfPanel" data-sentry-source-file="AppShelf.tsx">
            <div role="toolbar" onKeyDown={moveShelfFocus} className="flex flex-col gap-2">
                {records && <div className="flex flex-col gap-1 border-b border-ink/10 px-1 pb-2">
                        <span className="pl-1.5 text-caption text-ink-3">
                            {openLabel}
                        </span>
                        <div className="flex flex-wrap gap-1">{records}</div>
                    </div>}
                <div className="flex flex-wrap justify-center gap-0.5">
                    {apps}
                </div>
            </div>
        </GlassPanel>;
}